import { RivalComparison } from "./RivalComparison";
import { Admin } from "./Admin";
import { usePageTracking } from "./page-tracking";
import { ChoiceStats, EvaluationDetail, RoundStanding } from "./ResultInsights";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  useId,
} from "react";
import type { ReactNode, FormEvent } from "react";
import {
  House,
  ChartLineUp,
  Trophy,
  ChatsCircle,
  ClockCounterClockwise,
  ArrowUpRight,
  ArrowRight,
  ArrowLeft,
  Lightning,
  Infinity as InfinityIcon,
  Check,
  Lock,
  Plus,
  Bell,
  UserCircle,
  X,
  Info,
  SignOut,
  GearSix,
  ShieldCheck,
  Heart,
  ChatCircle,
  Flag,
  DownloadSimple,
  LinkSimple,
  CaretRight,
  CheckCircle,
  TrendUp,
  TrendDown,
  Minus,
  BookOpen,
  CalendarBlank,
  Medal,
  Target,
  Globe,
  SpinnerGap,
  Trash,
  Prohibit,
  PencilSimple,
} from "@phosphor-icons/react";
import {
  api,
  money,
  shortMoney,
  signed,
  pct,
  cn,
  labels,
  hints,
  modes,
} from "./api";
import { Chart } from "./Chart";
import { Battle } from "./Battle";
import { readCandles, writeCandles } from "./battle-state";
import { ChoiceSpectrum } from "./ChoiceSpectrum";
import { Confetti, CountUp } from "./effects";
import { PositionTrack } from "./PositionTrack";
import { bestChoiceCount, choiceTag } from "./round-review";
import { ResultExplanation } from "./ResultExplanation";
import { QuestionDiscussion } from "./QuestionDiscussion";
import { BrandMark, ResultMascot } from "./BrandMark";
const ErrorContext = createContext({ message: "", clear: () => {} });
// ---- URL 라우터: 페이지마다 고유 주소를 갖고, 탭 등 화면 상태는 쿼리에 보존 ----
const PAGE_PATHS: Record<string, string> = {
  home: "/",
  practice: "/practice",
  ranking: "/ranking",
  community: "/community",
  records: "/records",
  profile: "/profile",
  notifications: "/notifications",
  admin: "/admin",
  game: "/game",
  battle: "/battle",
};
type Route = {
  page: string;
  param: string;
  query: URLSearchParams;
  push: (to: string) => void;
  replace: (to: string) => void;
  back: (fallback: string) => void;
};
function parseRoute(url: string) {
  const u = new URL(url, location.origin);
  const [, seg = "", param = ""] = u.pathname.split("/");
  const page = seg === "" ? "home" : PAGE_PATHS[seg] ? seg : "home";
  return { page, param: decodeURIComponent(param), query: u.searchParams };
}
const currentUrl = () => location.pathname + location.search;
const historyIndex = () => Number(history.state?.idx) || 0;
function useRouter(): Route {
  const [url, setUrl] = useState(currentUrl);
  useEffect(() => {
    const onPop = () => setUrl(currentUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const push = useCallback((to: string) => {
    if (to === currentUrl()) return;
    history.pushState({ idx: historyIndex() + 1 }, "", to);
    setUrl(to);
  }, []);
  const replace = useCallback((to: string) => {
    history.replaceState(history.state, "", to);
    setUrl(to);
  }, []);
  const back = useCallback(
    (fallback: string) => {
      if (historyIndex() > 0) history.back();
      else push(fallback);
    },
    [push],
  );
  const parsed = parseRoute(url);
  return { ...parsed, push, replace, back };
}
const RouteContext = createContext<Route>({
  page: "home",
  param: "",
  query: new URLSearchParams(),
  push: () => {},
  replace: () => {},
  back: () => {},
});
// 탭·월 선택처럼 페이지 안의 상태를 쿼리에 기록해, 뒤로가기로 돌아와도 유지되게 함
function useQueryParam(key: string, fallback: string) {
  const route = useContext(RouteContext);
  const value = route.query.get(key) || fallback;
  const set = (v: string) => {
    const q = new URLSearchParams(location.search);
    if (v === fallback) q.delete(key);
    else q.set(key, v);
    const qs = q.toString();
    route.replace(location.pathname + (qs ? "?" + qs : ""));
  };
  return [value, set] as const;
}
const START = 100_000_000;
const NAV = [
  { id: "home", label: "홈", icon: House },
  { id: "practice", label: "자유 연습", icon: ChartLineUp },
  { id: "ranking", label: "월간 랭킹", icon: Trophy },
  { id: "community", label: "커뮤니티", icon: ChatsCircle },
  { id: "records", label: "나의 기록", icon: ClockCounterClockwise },
];
function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const feedback = useContext(ErrorContext);
  const titleId = useId();
  useEffect(() => {
    feedback.clear();
    ref.current?.showModal();
    return () => ref.current?.close();
  }, []);
  return (
    <dialog
      aria-labelledby={titleId}
      ref={ref}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-head">
        <h2 id={titleId}>{title}</h2>
        <button className="icon-btn" aria-label="닫기" onClick={onClose}>
          <X size={22} />
        </button>
      </div>
      {feedback.message && (
        <div role="alert" className="error-banner">
          <Info size={18} />
          {feedback.message}
          <button
            className="icon-btn"
            aria-label="오류 닫기"
            onClick={feedback.clear}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {children}
    </dialog>
  );
}
function Empty({
  icon: Icon = BookOpen,
  title,
  body,
  action,
}: {
  icon?: any;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon size={34} weight="duotone" />
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}
function Loader() {
  return (
    <div className="loading">
      <SpinnerGap size={28} className="spin" />
      <span>불러오는 중이에요</span>
    </div>
  );
}
function Avatar({ name, size = "" }: { name: string; size?: string }) {
  return <span className={`avatar ${size}`}>{name.slice(0, 1)}</span>;
}
export default function App() {
  const route = useRouter();
  const page = route.page,
    roundId = page === "game" ? route.param : "",
    profileParam = page === "profile" ? route.param : "";
  const [boot, setBoot] = useState<any>(null),
    [round, setRound] = useState<any>(null),
    [reveal, setReveal] = useState(false),
    [summary, setSummary] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [toast, setToast] = useState(""),
    [modal, setModal] = useState<string | null>(null);
  usePageTracking(page, route.param, !!boot);
  const refresh = useCallback(async () => {
    const b = await api("/bootstrap");
    setBoot(b);
    return b;
  }, []);
  useEffect(() => {
    refresh().catch((e) => setError(e.message));
    const timer = setInterval(() => refresh().catch(() => {}), 60000);
    const visible = () => {
      if (document.visibilityState === "visible") refresh().catch(() => {});
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refresh]);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(""), 3200);
      return () => clearTimeout(t);
    }
  }, [toast]);
  useEffect(() => {
    if (round?.mode === "practice" && round.status === "active") {
      const abandon = () =>
        navigator.sendBeacon(`/api/rounds/${round.id}/abandon`);
      window.addEventListener("pagehide", abandon);
      return () => window.removeEventListener("pagehide", abandon);
    }
  }, [round]);
  useEffect(() => {
    const label =
      page === "battle"
        ? "친구 대결"
        : page === "game"
          ? modes[round?.mode] || "게임"
          : page === "profile"
            ? "투자 프로필"
            : page === "admin"
              ? "운영 관리"
              : page === "notifications"
                ? "알림"
                : NAV.find((n) => n.id === page)?.label;
    document.title = page === "home" || !label ? "개미톡" : `${label} · 개미톡`;
  }, [page, round?.mode]);
  // 게임 화면을 벗어나면(메뉴 이동·뒤로가기 모두) 진행 중인 연습 라운드를 정리
  const prevPage = useRef(page);
  useEffect(() => {
    const was = prevPage.current;
    prevPage.current = page;
    if (
      was === "game" &&
      page !== "game" &&
      round?.mode === "practice" &&
      round.status === "active"
    ) {
      api(`/rounds/${round.id}/abandon`, {}).catch((e: any) =>
        setError(e.message),
      );
      setRound(null);
    }
  }, [page]);
  // 주소로 직접 들어오거나 뒤로/앞으로 이동한 경우 URL의 라운드를 불러옴
  useEffect(() => {
    if (page !== "game") return;
    if (!roundId) {
      route.replace("/");
      return;
    }
    if (round?.id === roundId) return;
    let cancelled = false;
    api("/rounds/" + roundId)
      .then((r) => {
        if (cancelled) return;
        setRound(r);
        setReveal(false);
        setSummary(r.status !== "active");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        route.replace("/");
      });
    return () => {
      cancelled = true;
    };
  }, [page, roundId, round?.id]);
  const run = async (fn: () => Promise<any>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      return await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const logged = boot?.user.role !== "guest";
  const requireLogin = () => {
    if (!logged) {
      setModal("login");
      return false;
    }
    return true;
  };
  const navigate = (id: string) => {
    route.push(id.startsWith("/community?") ? id : PAGE_PATHS[id] || "/");
    setError("");
    window.scrollTo({ top: 0, behavior: "instant" });
    if (
      ["records", "profile", "notifications", "admin"].includes(id) &&
      !logged
    )
      setModal("login");
  };
  const start = (mode: string, questionId?: string) => {
    if (mode !== "tutorial" && !requireLogin()) return;
    run(async () => {
      const r = await api("/rounds", { mode, questionId });
      setRound(r);
      setReveal(r.status === "active" && r.results.length > 0);
      setSummary(r.status === "completed");
      route.push(`/game/${r.id}`);
      await refresh();
      window.scrollTo(0, 0);
    });
  };
  const resume = (id: string) => {
    if (busy) return;
    setError("");
    setRound(null); // 항상 서버의 최신 상태를 다시 불러옴
    route.push(`/game/${id}`);
    window.scrollTo(0, 0);
  };
  const answer = (action: string) =>
    run(async () => {
      const r = await api(`/rounds/${round.id}/answer`, {
        index: round.index,
        action,
      });
      setRound(r);
      setReveal(true);
      await refresh();
    });
  const next = () => {
    setReveal(false);
    if (round.status === "completed") setSummary(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const profile = (id: string) => {
    route.push(id === boot?.user.id ? "/profile" : `/profile/${id}`);
    setError("");
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const notify = (s: string) => setToast(s);
  const openPost = (id: string) => {
    route.push(`/community/${id}`);
    setError("");
  };
  if (!boot)
    return (
      <div className="boot">
        <BrandMark size={64} />
        {error ? (
          <>
            <h2>연결을 확인해 주세요</h2>
            <p>{error}</p>
            <button
              className="btn primary"
              onClick={() => refresh().catch((e) => setError(e.message))}
            >
              다시 시도
            </button>
          </>
        ) : (
          <Loader />
        )}
      </div>
    );
  return (
    <RouteContext.Provider value={route}>
      <ErrorContext.Provider
        value={{ message: error, clear: () => setError("") }}
      >
        <div className={`app-shell ${page === "game" ? "game-active" : ""}`}>
          <aside className="sidebar">
            <button className="brand" onClick={() => navigate("home")}>
              <BrandMark size={44} />
              <span>
                개미톡<small>선택하면서, 배워가는 투자</small>
              </span>
            </button>
            <div className="sidebar-label">나의 개미톡</div>
            <nav>
              {NAV.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  className={`nav-item ${page === id ? "active" : ""}`}
                  onClick={() => navigate(id)}
                >
                  <Icon size={22} weight={page === id ? "fill" : "regular"} />
                  {label}
                  {id === "practice" && <span className="nav-new">무제한</span>}
                </button>
              ))}
            </nav>
            <div className="sidebar-bottom">
              <div className="season-note">
                <span className="season-dot" />
                <span>{Number(boot.month.slice(5))}월 시즌 진행 중</span>
                <small>새로운 달, 새로운 출발</small>
              </div>
              {boot.user.role === "admin" && (
                <button
                  className="nav-item admin-link"
                  onClick={() => navigate("admin")}
                >
                  <GearSix size={20} />
                  운영 관리
                </button>
              )}
              <button
                className="account"
                onClick={() =>
                  logged ? profile(boot.user.id) : setModal("login")
                }
              >
                <Avatar name={logged ? boot.user.name : "?"} />
                <span>
                  {logged ? boot.user.name : "로그인하고 시작하기"}
                  <small>
                    {logged ? "나의 프로필" : "나만의 투자 기록을 남겨보세요"}
                  </small>
                </span>
                <CaretRight size={16} />
              </button>
            </div>
          </aside>
          <div className="main-shell">
            <header className="topbar">
              <button className="mobile-brand" onClick={() => navigate("home")}>
                <BrandMark size={34} />
                개미톡
              </button>
              <div className="top-breadcrumb">
                {page === "battle"
                  ? "친구 대결"
                  : page === "game"
                    ? modes[round?.mode]
                    : page === "profile"
                      ? "투자 프로필"
                      : page === "admin"
                        ? "운영 관리"
                        : NAV.find((n) => n.id === page)?.label || "알림"}
              </div>
              <div className="top-actions">
                {boot.demo && <span className="preview-tag">로컬 프리뷰</span>}
                <button
                  className="icon-btn notification-btn"
                  aria-label="알림"
                  onClick={() => navigate("notifications")}
                >
                  <Bell size={22} />
                  {boot.notifications > 0 && <i />}
                </button>
                <button
                  aria-label={logged ? "내 프로필" : "로그인"}
                  className="top-profile"
                  onClick={() =>
                    logged ? profile(boot.user.id) : setModal("login")
                  }
                >
                  {logged ? (
                    <Avatar name={boot.user.name} size="small" />
                  ) : (
                    <>
                      <UserCircle size={22} />
                      <span>로그인</span>
                    </>
                  )}
                </button>
              </div>
            </header>
            <main id="main">
              {error && (
                <div role="alert" className="error-banner">
                  <Info size={19} />
                  {error}
                  <button
                    className="icon-btn"
                    aria-label="오류 닫기"
                    onClick={() => setError("")}
                  >
                    <X size={17} />
                  </button>
                </div>
              )}
              {page === "home" && (
                <Home
                  boot={boot}
                  start={start}
                  navigate={navigate}
                  resume={resume}
                  busy={busy}
                  openPost={openPost}
                />
              )}
              {page === "battle" && (
                <Battle
                  key={route.param || "new"}
                  code={route.param}
                  navigate={route.push}
                  defaultName={logged ? boot.user.name : ""}
                />
              )}
              {page === "practice" && (
                <Practice
                  boot={boot}
                  start={start}
                  navigate={navigate}
                  busy={busy}
                />
              )}
              {page === "game" && round?.id !== roundId && <Loader />}
              {page === "game" && round && round.id === roundId && (
                <Game
                  key={round.id}
                  round={round}
                  reveal={reveal}
                  summary={summary}
                  answer={answer}
                  next={next}
                  busy={busy}
                  exit={() => navigate("home")}
                  start={start}
                  notify={notify}
                  requireLogin={requireLogin}
                  onError={setError}
                />
              )}
              {page === "ranking" && (
                <Ranking boot={boot} profile={profile} start={start} />
              )}
              {page === "community" && (
                <Community
                  requireLogin={requireLogin}
                  onError={setError}
                  notify={notify}
                  profile={profile}
                />
              )}
              {page === "records" &&
                (logged ? (
                  <Records resume={resume} start={start} />
                ) : (
                  <LoginGate open={() => setModal("login")} />
                ))}
              {page === "profile" &&
                (logged || profileParam ? (
                  <Profile
                    key={profileParam || boot.user.id}
                    id={profileParam || boot.user.id}
                    mine={(profileParam || boot.user.id) === boot.user.id}
                    refresh={refresh}
                    notify={notify}
                    onError={setError}
                    logout={() =>
                      run(async () => {
                        await api("/auth/logout", {});
                        route.replace("/");
                        await refresh();
                      })
                    }
                  />
                ) : (
                  <LoginGate open={() => setModal("login")} />
                ))}
              {page === "notifications" &&
                (logged ? (
                  <Notifications refresh={refresh} navigate={navigate} />
                ) : (
                  <LoginGate open={() => setModal("login")} />
                ))}
              {page === "admin" && (
                <Admin
                  query={route.query}
                  replace={route.replace}
                  notify={notify}
                  refresh={refresh}
                />
              )}
            </main>
            <footer className="footer">
              <span>오늘의 선택이, 내일의 감각으로.</span>
              <span className="footer-links">
                <a href="/privacy.html">개인정보처리방침</a>
                <a href="/terms.html">이용약관</a>
                <span>가상 자산으로 즐기는 과거 시장 게임</span>
              </span>
            </footer>
          </div>
          <nav className="mobile-nav">
            {NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={page === id ? "active" : ""}
                onClick={() => navigate(id)}
              >
                <Icon size={22} weight={page === id ? "fill" : "regular"} />
                <span>{label}</span>
              </button>
            ))}
          </nav>
          {modal === "login" && (
            <Modal
              title="나의 투자 감각을 발견해요"
              onClose={() => setModal(null)}
            >
              <BrandMark
                expression="welcome"
                size={80}
                className="login-mascot"
              />
              <p className="modal-copy">
                매일 새로운 도전과 무제한 연습.
                <br />
                로그인하면 모든 선택이 나의 기록으로 남아요.
              </p>
              <div className="login-options">
                <button
                  className="btn kakao"
                  disabled={!boot.providers.kakao}
                  onClick={() => location.assign("/api/auth/kakao")}
                >
                  <ChatCircle size={20} weight="fill" />
                  카카오로 계속하기
                </button>
                <button
                  className="btn outline"
                  disabled={!boot.providers.google}
                  onClick={() => location.assign("/api/auth/google")}
                >
                  <Globe size={20} />
                  구글로 계속하기
                </button>
                {(!boot.providers.kakao || !boot.providers.google) && (
                  <p className="caption">
                    소셜 로그인은 서비스 연결 설정 후 사용할 수 있어요.
                  </p>
                )}
                {boot.demo && (
                  <>
                    <div className="divider-text">로컬에서 먼저 둘러보기</div>
                    <button
                      disabled={busy}
                      className="btn primary"
                      onClick={() =>
                        run(async () => {
                          await api("/auth/demo", {});
                          await refresh();
                          setModal(null);
                          notify("로컬 체험 계정으로 시작했어요.");
                        })
                      }
                    >
                      체험 계정으로 시작 <ArrowRight size={18} />
                    </button>
                    <p className="caption">
                      이 기기에 연결된 개발 서버에 기록이 저장됩니다.
                    </p>
                  </>
                )}
              </div>
            </Modal>
          )}
          {toast && (
            <div role="status" className="toast">
              <CheckCircle size={20} weight="fill" />
              {toast}
            </div>
          )}
        </div>
      </ErrorContext.Provider>
    </RouteContext.Provider>
  );
}
function LoginGate({ open }: { open: () => void }) {
  return (
    <Empty
      icon={Lock}
      title="나의 선택을 기록으로 남겨요"
      body="로그인하면 게임 기록과 지난 시즌의 성적을 언제든 확인할 수 있어요."
      action={
        <button className="btn primary" onClick={open}>
          로그인하기 <ArrowRight size={18} />
        </button>
      }
    />
  );
}
function Home({ boot, start, navigate, resume, busy, openPost }: any) {
  const date = new Date(boot.today + "T12:00:00+09:00");
  return (
    <div className="home-page">
      <div className="page-heading">
        <div>
          <div className="date-line">
            {date.toLocaleDateString("ko-KR", {
              month: "long",
              day: "numeric",
              weekday: "long",
            })}
          </div>
          <div className="welcome-heading">
            <BrandMark expression="welcome" size={52} />
            <h1>오늘은 어떤 선택을 해볼까요?</h1>
          </div>
          <p>실제 시장의 순간으로 돌아가, 나만의 투자 감각을 키워보세요.</p>
        </div>
        <span className="quiet-chip">
          <CalendarBlank size={16} />
          {Number(boot.month.slice(5))}월 시즌
        </span>
      </div>
      <button className="battle-home-link" onClick={() => navigate("battle")}>
        <span>
          <strong>친구와 투자 한 판</strong>
          <small>같은 5문제, 누가 더 잘 투자할까요? · 2~4명</small>
        </span>
        <ArrowRight size={22} />
      </button>
      <div className="home-columns">
        <div className="home-main">
          <section className="challenge-hero">
            <div className="hero-content">
              <span className="eyebrow">
                <Lightning size={15} weight="fill" />
                오늘의 도전
              </span>
              <h2>
                만약 그때,
                <br />
                내가 투자했다면?
              </h2>
              <p>
                1억 원으로 시작하는 다섯 번의 선택.
                <br />
                지나간 시장 속에서 새로운 가능성을 만나보세요.
              </p>
              <button
                className="btn primary hero-button"
                disabled={busy || boot.daily.available === 0}
                onClick={() =>
                  boot.daily.active
                    ? resume(boot.daily.active)
                    : start(
                        boot.daily.used >= 3
                          ? "practice"
                          : boot.user.role === "guest"
                            ? "tutorial"
                            : "daily",
                      )
                }
              >
                {boot.daily.active
                  ? "이어서 도전하기"
                  : boot.daily.used >= 3
                    ? "자유 연습 계속하기"
                    : boot.user.role === "guest"
                      ? "무료로 한 판 체험하기"
                      : "오늘의 도전 시작하기"}
                <ArrowRight size={20} />
              </button>
              <div className="hero-meta">
                <span>
                  <Check size={14} />
                  실제 과거 데이터
                </span>
                <span>
                  <Check size={14} />
                  5문제 · 가상 투자금
                </span>
              </div>
            </div>
            <div className="hero-art" aria-hidden="true">
              <div className="art-window">
                <div className="art-top">
                  <span className="art-dot" />
                  나의 다음 선택<span>↗</span>
                </div>
                <div className="art-number">
                  100,000,000<span>원</span>
                </div>
                <div className="art-hint">새로운 가능성의 시작</div>
                <svg viewBox="0 0 300 145">
                  <defs>
                    <linearGradient id="hero-area" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#876aff" stopOpacity=".25" />
                      <stop offset="100%" stopColor="#876aff" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0 123 L24 110 L44 116 L62 92 L84 100 L108 72 L129 84 L149 56 L169 64 L192 29 L213 42 L240 15 L240 145 L0 145Z"
                    fill="url(#hero-area)"
                  />
                  <path
                    d="M0 123 L24 110 L44 116 L62 92 L84 100 L108 72 L129 84 L149 56 L169 64 L192 29 L213 42 L240 15"
                    fill="none"
                    stroke="#8974ee"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M240 15 L280 4"
                    stroke="#a892f7"
                    strokeWidth="3"
                    strokeDasharray="5 6"
                  />
                  <circle cx="240" cy="15" r="8" fill="#dfd8ff" />
                  <circle cx="240" cy="15" r="4" fill="#8069ec" />
                </svg>
                <div className="art-foot">
                  <span>과거의 흐름</span>
                  <span>나의 선택</span>
                </div>
              </div>
              <div className="floating-question">?</div>
              <div className="floating-choice">
                <TrendUp size={19} />
                <span>기회일까?</span>
              </div>
            </div>
          </section>
          <section className="daily-section">
            <div className="section-heading">
              <h2>
                오늘의 세 판
                <span className="count-pill">{boot.daily.completed} / 3</span>
              </h2>
              <span className="caption">매일 밤 12시 새롭게 시작해요</span>
            </div>
            <div className="daily-grid">
              {[0, 1, 2].map((i) => {
                const done = i < boot.daily.completed,
                  active = i === boot.daily.completed && boot.daily.used < 4,
                  locked = i > boot.daily.completed;
                return (
                  <button
                    key={i}
                    disabled={locked || done || busy || !boot.daily.available}
                    className={`daily-card ${active ? "current" : ""} ${done ? "done" : ""}`}
                    onClick={() =>
                      boot.daily.active
                        ? resume(boot.daily.active)
                        : start("daily")
                    }
                  >
                    <span className="daily-card-top">
                      <span className="round-number">0{i + 1}</span>
                      {done ? (
                        <CheckCircle size={21} weight="fill" />
                      ) : locked ? (
                        <Lock size={18} />
                      ) : (
                        <ArrowUpRight size={20} />
                      )}
                    </span>
                    <strong>
                      {["첫 번째 선택", "흐름을 읽는 시간", "마지막 한 수"][i]}
                    </strong>
                    <span className="daily-card-bottom">
                      {done
                        ? "도전 완료"
                        : locked
                          ? "이전 판 완료 후 열려요"
                          : "5문제 · 1억 원으로 시작"}
                      {active && <i />}
                    </span>
                  </button>
                );
              })}
            </div>
            {boot.daily.reissued && (
              <p className="caption reissue">
                <Info size={14} />
                오늘은 이전에 출제된 사례가 포함되어 있어요.
              </p>
            )}
          </section>
          <button
            className="practice-banner"
            onClick={() => navigate("practice")}
          >
            <span className="practice-icon">
              <InfinityIcon size={28} />
            </span>
            <span>
              <strong>조금 더 연습하고 싶다면</strong>
              <small>순위 부담 없이, 내 페이스대로 무제한 연습</small>
            </span>
            <ArrowRight size={22} />
          </button>
          <HomePosts navigate={navigate} openPost={openPost} />
        </div>
        <aside className="home-rail">
          <section className="panel my-season">
            <div className="section-heading">
              <h2>이번 달 나의 기록</h2>
              <Trophy size={22} className="muted" />
            </div>
            <span className="caption">월간 누적 손익</span>
            <div className={`season-profit ${cn(boot.stats.profit)}`}>
              {signed(boot.stats.profit)}
            </div>
            <div className="season-detail">
              <div>
                <span>현재 순위</span>
                <strong>
                  {boot.stats.rank ? `${boot.stats.rank}위` : "아직 도전 전"}
                </strong>
              </div>
              <div>
                <span>오늘 완료</span>
                <strong>
                  {boot.daily.completed}
                  <small> / 3판</small>
                </strong>
              </div>
            </div>
            <button className="text-button" onClick={() => navigate("ranking")}>
              이번 달 랭킹 보기
              <CaretRight size={16} />
            </button>
            <p className="season-foot">
              <ShieldCheck size={15} />
              지난 시즌의 기록은 계속 남아요
            </p>
          </section>
          <section className="how-panel">
            <span className="soft-icon">
              <BookOpen size={24} weight="duotone" />
            </span>
            <h2>처음이어도 괜찮아요</h2>
            <p>
              주식 이름은 잠깐 가려둘게요.
              <br />
              차트와 상황을 보고 선택해 보세요.
            </p>
            <ol>
              <li>
                <span>1</span>
                <div>
                  <strong>상황을 살펴보고</strong>
                  <small>당시의 차트와 짧은 배경 읽기</small>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>나만의 선택을 하고</strong>
                  <small>매도, 유지, 추가 매수 중 선택</small>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>실제 결과로 돌아보기</strong>
                  <small>다른 선택의 결과도 함께 확인</small>
                </div>
              </li>
            </ol>
            <button
              className="text-button"
              onClick={() =>
                start(boot.user.role === "guest" ? "tutorial" : "practice")
              }
            >
              가볍게 연습해 보기 <ArrowUpRight size={16} />
            </button>
          </section>
          <div className="small-note">
            <Info size={17} />
            <p>
              실제 돈을 사용하지 않아요.
              <br />
              과거의 결과는 미래의 수익을
              <br className="desktop-only" /> 보장하지 않습니다.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
function HomePosts({ navigate, openPost }: any) {
  const [posts, setPosts] = useState<any[]>([]);
  useEffect(() => {
    api("/posts")
      .then(setPosts)
      .catch(() => {});
  }, []);
  return (
    <section className="home-posts">
      <div className="section-heading">
        <h2>함께 나누는 투자 이야기</h2>
        <button
          className="text-button muted"
          onClick={() => navigate("community")}
        >
          전체 보기
          <CaretRight size={15} />
        </button>
      </div>
      {[...posts]
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 2)
        .map((p) => (
          <button
            className="home-post"
            key={p.id}
            onClick={() => openPost(p.id)}
          >
            <span className="post-category">{p.category}</span>
            <span>{p.title}</span>
            <span className="post-count">
              <ChatCircle size={16} />
              {p.comments}
            </span>
          </button>
        ))}
    </section>
  );
}
function Practice({ boot, start, navigate, busy }: any) {
  return (
    <>
      <div className="page-heading">
        <div>
          <div className="date-line">나의 속도로, 나의 방식으로</div>
          <h1>경쟁은 잠시, 연습은 마음껏.</h1>
          <p>더 많이 선택하고 돌아볼수록 나만의 투자 감각이 쌓여요.</p>
        </div>
      </div>
      <section className="practice-feature">
        <div>
          <span className="eyebrow">
            <InfinityIcon size={17} />
            무제한 자유 연습
          </span>
          <h2>
            틀려도 괜찮은
            <br />
            투자의 연습장
          </h2>
          <p>
            국내·미국의 실제 과거 사례를 랜덤으로 만나요.
            <br />
            아직 풀지 않은 문제부터 준비해드릴게요.
          </p>
          <button
            className="btn primary"
            disabled={busy}
            onClick={() => start("practice")}
          >
            새로운 연습 시작 <ArrowRight size={20} />
          </button>
          <small>한 판 5문제 · 시작 자산 1억 원 · 순위에 반영되지 않아요</small>
        </div>
        <div className="practice-orbit" aria-hidden="true">
          <div className="orbit orbit-one" />
          <div className="orbit orbit-two" />
          <div className="orbit-core">
            <ChartLineUp size={74} weight="duotone" />
          </div>
          <span className="orbit-badge">
            <InfinityIcon size={24} /> 나의 가능성
          </span>
        </div>
      </section>
      <div className="practice-info-grid">
        <section className="panel">
          <Target size={24} />
          <h3>결과보다 판단을 돌아봐요</h3>
          <p>
            네 선택의 결과를 비교하고, 내가 어떤 상황에서 사고팔았는지 발견해
            보세요.
          </p>
        </section>
        <section className="panel">
          <ClockCounterClockwise size={24} />
          <h3>지난 선택도 다시 한번</h3>
          <p>
            나의 기록에서 문제를 다시 풀 수 있어요. 중단한 판은 종합 성적에서
            제외됩니다.
          </p>
          <button className="text-button" onClick={() => navigate("records")}>
            내 연습 기록 보기 <ArrowRight size={16} />
          </button>
        </section>
      </div>
      <div className="inline-info">
        <CheckCircle size={19} />
        {boot.stats.practiceCount}판의 연습을 완료했어요. 작은 선택들이 경험이
        됩니다.
      </div>
    </>
  );
}
function Game({
  round,
  reveal,
  summary,
  answer,
  next,
  busy,
  exit,
  start,
  notify,
  requireLogin,
  onError,
}: any) {
  const [candles, setCandles] = useState(() => readCandles(() => localStorage)),
    [animating, setAnimating] = useState(false),
    [discussion, setDiscussion] = useState(false),
    [discussionCounts, setDiscussionCounts] = useState<Record<string, number>>(
      {},
    ),
    [detail, setDetail] = useState<number | null>(null);
  const chooseCandles = (value: boolean) => {
    setCandles(value);
    writeCandles(() => localStorage, value);
  };
  useEffect(() => {
    setDiscussion(false);
    if (reveal && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 2600);
      return () => clearTimeout(timer);
    }
    setAnimating(false);
  }, [reveal, round.index]);
  const currentResult =
      detail !== null ? round.results[detail] : round.results.at(-1),
    resultMode = reveal || detail !== null;
  const q = resultMode ? currentResult.question : round.current;
  const share = async () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#f4f2fd";
      ctx.fillRect(0, 0, 1080, 1080);
      ctx.fillStyle = "#fff";
      ctx.fillRect(60, 60, 960, 960);
      ctx.fillStyle = "#856aef";
      ctx.font = "bold 34px sans-serif";
      ctx.fillText("개미톡 · " + modes[round.mode], 120, 160);
      ctx.fillStyle = "#1e1636";
      ctx.font = "bold 52px sans-serif";
      ctx.fillText("다섯 번의 선택, 나의 결과", 120, 270);
      ctx.font = "bold 70px sans-serif";
      ctx.fillText(money(round.assets), 120, 390);
      ctx.fillStyle = round.profit >= 0 ? "#db5768" : "#397bed";
      ctx.font = "bold 40px sans-serif";
      ctx.fillText(pct((round.profit / START) * 100), 120, 465);
      ctx.font = "28px sans-serif";
      round.results.forEach((r: any, i: number) => {
        ctx.fillStyle = "#6b618b";
        ctx.fillText(
          `${i + 1}번째 선택   ${labels[r.action]}`,
          120,
          560 + i * 60,
        );
        ctx.fillStyle = "#1e1636";
        ctx.fillText(signed(r.profit), 590, 560 + i * 60);
      });
      ctx.fillStyle = "#817b9c";
      ctx.font = "24px sans-serif";
      ctx.fillText("실제 과거 시장으로 즐기는 가상 투자 게임", 120, 955);
      const a = document.createElement("a");
      a.download = "나의-투자-기록.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
      notify("공유 이미지를 저장했어요.");
    } catch {
      notify("이미지를 저장하지 못했어요. 다시 시도해 주세요.");
    }
  };
  if (summary && detail === null)
    return (
      <div className="result-page">
        <button className="back-button" onClick={exit}>
          <ArrowLeft size={18} />
          홈으로
        </button>
        <div className="result-intro">
          <span className="result-medal">
            <CheckCircle size={44} weight="duotone" />
          </span>
          <div className="date-line">
            {modes[round.mode]} ·{" "}
            {round.status === "abandoned"
              ? "중단된 연습"
              : `${round.results.length}번의 선택 완료`}
          </div>
          <h1>나의 선택이 만든 결과</h1>
          <p>
            {round.mode === "daily"
              ? "이번 판의 손익이 월간 성적에 반영되었어요."
              : "순위 부담 없이, 오늘의 선택을 돌아보세요."}
          </p>
        </div>
        <section className="panel final-assets">
          <span className="caption">최종 가상 자산</span>
          <h2>{money(round.assets)}</h2>
          <strong className={cn(round.profit)}>
            {signed(round.profit)}{" "}
            <span>({pct((round.profit / START) * 100)})</span>
          </strong>
          <div className="result-start">시작 자산 {money(START)}</div>
        </section>
        {round.rivals && <RivalComparison data={round.rivals} />}
        <RoundStanding standing={round.standing} />
        {round.extremes && (
          <section className="panel">
            <h2>이번 판, 최고와 최저 사이 나는 어디쯤?</h2>
            <PositionTrack
              best={round.extremes.best}
              worst={round.extremes.worst}
              players={[
                {
                  id: "me",
                  name: "나",
                  assets: round.assets,
                  rank: 1,
                  me: true,
                },
              ]}
              note={`5문제 중 ${bestChoiceCount(
                round.results.map((r: any) => r.action),
                round.extremes,
              )}문제에서 최고의 선택을 했어요.`}
            />
          </section>
        )}
        <section className="panel result-list">
          <h2>다섯 번의 선택 돌아보기</h2>
          {round.results.map((r: any, i: number) => (
            <button key={i} onClick={() => setDetail(i)}>
              <span className="index-circle">{i + 1}</span>
              <span>
                <strong>{r.question.company}</strong>
                <small>
                  {labels[r.action]}
                  {r.automatic ? " · 마감 자동 선택" : ""}
                  {r.invalid ? " · 무효 정산" : ""}
                  {round.extremes &&
                    {
                      best: " · 최고의 선택",
                      worst: " · 최악의 선택",
                      none: "",
                    }[choiceTag(r.action, round.extremes, i) ?? "none"]}
                </small>
                <small>{r.evaluation?.message}</small>
              </span>
              <span className={cn(r.profit)}>{signed(r.profit)}</span>
              <CaretRight size={17} />
            </button>
          ))}
        </section>
        <div className="share-buttons">
          <button className="btn outline" onClick={share}>
            <DownloadSimple size={19} />
            결과 이미지 저장
          </button>
          <button
            className="btn outline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(location.origin);
                notify("서비스 링크를 복사했어요.");
              } catch {
                notify("브라우저 주소를 복사해 공유해 주세요.");
              }
            }}
          >
            <LinkSimple size={19} />
            링크 복사
          </button>
        </div>
        <button
          className="btn primary full"
          onClick={() =>
            start(
              round.mode === "daily" && round.slot < 2 ? "daily" : "practice",
            )
          }
        >
          {round.mode === "daily" && round.slot < 2
            ? "다음 도전으로"
            : "새로운 연습 시작"}
          <ArrowRight size={20} />
        </button>
        <button className="text-button centered" onClick={exit}>
          홈으로 돌아가기
        </button>
      </div>
    );
  if (!q) return <Loader />;
  const shownAssets = resultMode ? currentResult.after : round.assets;
  const actionIcons: Record<string, any> = {
    sell: TrendDown,
    half: Minus,
    hold: Check,
    buy: Plus,
  };
  return (
    <div
      className={`game-page ${resultMode ? "showing-result" : "showing-question"}`}
    >
      <div className="game-top">
        <button
          className="back-button"
          onClick={() => (detail !== null ? setDetail(null) : exit())}
        >
          <ArrowLeft size={19} />
          {detail !== null
            ? "결과 요약"
            : round.mode === "daily"
              ? "나가기 · 진행 저장"
              : "연습 종료"}
        </button>
        <span className="quiet-chip">{modes[round.mode]}</span>
      </div>
      <div className="game-columns">
        <section className="panel chart-panel">
          <h1>
            {resultMode
              ? q.company
              : `${q.market === "KR" ? "국내" : "미국"} ${q.sector} 기업`}
          </h1>
          {resultMode ? (
            <>
              <div className="chart-price">
                {q.entryPrice.toLocaleString("ko-KR", {
                  maximumFractionDigits: 2,
                })}{" "}
                →{" "}
                {q.exitPrice.toLocaleString("ko-KR", {
                  maximumFractionDigits: 2,
                })}
                <small>{q.currency === "KRW" ? "원" : "달러"}</small>
              </div>
              <div className={cn(currentResult.rate)}>
                {pct(currentResult.rate * 100)}{" "}
                <span className="caption">· {q.horizon}거래일 후</span>
              </div>
            </>
          ) : (
            <>
              <div className="chart-price">
                {q.past.at(-1).close.toFixed(2)}
                <small>시작점 100 기준</small>
              </div>
              <div className={cn(q.past.at(-1).close - 100)}>
                {pct(q.past.at(-1).close - 100)}{" "}
                <span className="caption">· 최근 {q.lookback - 1}거래일</span>
              </div>
            </>
          )}
          <div className="chart-tools">
            <div className="segmented small">
              <button
                className={!candles ? "selected" : ""}
                onClick={() => chooseCandles(false)}
              >
                선
              </button>
              <button
                className={candles ? "selected" : ""}
                onClick={() => chooseCandles(true)}
              >
                캔들·거래량
              </button>
            </div>
          </div>
          <Chart
            key={`${round.id}-${detail ?? (reveal ? round.index - 1 : round.index)}`}
            playback
            horizon={q.horizon}
            past={q.past}
            future={resultMode ? q.future : []}
            candles={candles}
            animate={animating}
          />
          <div className="asset-strip">
            <span>나의 자산</span>
            <strong>{money(shownAssets)}</strong>
          </div>
        </section>
        <section className="panel decision-panel">
          {resultMode ? (
            <>
              <ResultMascot
                profit={currentResult.evaluation?.tone === "positive" ? 1 : 0}
                invalid={!currentResult.evaluation?.assessed}
              />
              <span className="eyebrow">{labels[currentResult.action]}</span>
              <h2>
                {currentResult.evaluation?.message || "이번 선택을 돌아보세요"}
              </h2>
              {!animating && currentResult.profit > 0 && <Confetti />}
              <div
                className={`question-profit ${cn(currentResult.profit)}`}
                key={`${round.id}-${detail ?? round.index}`}
              >
                <CountUp value={currentResult.profit} />
              </div>
              <h3 className="compare-title">
                최고의 선택과 최악의 선택 사이, 나는 어디쯤?
              </h3>
              <ChoiceSpectrum
                outcomes={currentResult.comparisons.map((c: any) => ({
                  action: c.id,
                  value: c.profit,
                }))}
                mine={currentResult.action}
                format={signed}
              />
              <details className="original-question">
                <summary>당시 문제 다시 보기</summary>
                <h3>{q.title}</h3>
                <p>{q.context}</p>
              </details>
              <ResultExplanation
                explanation={q.briefExplanation || q.explanation}
                followUp={q.followUp}
              />
              <EvaluationDetail evaluation={currentResult.evaluation} />
              <ChoiceStats
                stats={q.stats}
                action={currentResult.action}
                automatic={currentResult.automatic}
              />
              {currentResult.invalid && (
                <div className="inline-info">
                  이 문제는 오류로 무효 처리되어 손익이 0원이에요.
                </div>
              )}
              {animating ? (
                <button
                  className="btn primary full"
                  onClick={() => setAnimating(false)}
                >
                  연출 건너뛰기 <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  className="btn primary full"
                  onClick={() => (detail !== null ? setDetail(null) : next())}
                >
                  {detail !== null
                    ? "결과 요약으로"
                    : round.status === "completed"
                      ? "한 판 결과 보기"
                      : "다음 문제"}
                  <ArrowRight size={19} />
                </button>
              )}
              <button
                className="text-button centered game-discussion-toggle"
                onClick={() => setDiscussion(!discussion)}
              >
                <ChatCircle size={18} />이 문제의 투자 이야기 ·{" "}
                {discussionCounts[q.id] ?? q.discussionCount ?? 0}{" "}
                {discussion ? "접기" : "펼치기"}
              </button>
            </>
          ) : (
            <>
              <p className="scenario">{q.context}</p>
              <div className="holdings">
                <div>
                  <span>보유 주식</span>
                  <strong>{shortMoney(shownAssets / 2)}</strong>
                </div>
                <div>
                  <span>남은 현금</span>
                  <strong>{shortMoney(shownAssets / 2)}</strong>
                </div>
              </div>
              <div className="action-grid">
                {["sell", "buy", "half", "hold"].map((id) => {
                  const Icon = actionIcons[id];
                  return (
                    <button
                      key={id}
                      disabled={busy}
                      className={`action-button ${id}`}
                      onClick={() => {
                        setAnimating(true);
                        answer(id);
                      }}
                    >
                      <span>
                        <Icon size={22} />
                        {labels[id]}
                      </span>
                      <small>
                        {hints[id]} · 주식 비중{" "}
                        {({ sell: 0, half: 25, hold: 50, buy: 100 } as any)[id]}
                        %
                      </small>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>
      {discussion && resultMode && (
        <section className="panel discussion-panel">
          <QuestionDiscussion
            question={q.id}
            onCountChange={(count) =>
              setDiscussionCounts((previous) => ({
                ...previous,
                [q.id]: count,
              }))
            }
            requireLogin={requireLogin}
            onError={onError}
            notify={notify}
          />
        </section>
      )}
    </div>
  );
}
function Ranking({ boot, profile, start }: any) {
  const [month, setMonth] = useQueryParam("month", boot.month);
  const [data, setData] = useState<any>(null),
    [error, setError] = useState("");
  const [seasons, setSeasons] = useState<string[]>([boot.month]);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    api("/ranking?month=" + month)
      .then((result) => {
        if (active) {
          setData(result);
          setSeasons(result.seasons);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [month, boot.user.id, retry]);
  return (
    <>
      <div className="page-heading">
        <div>
          <div className="date-line">함께 즐기는 월간 시즌</div>
          <h1>{Number(month.slice(5))}월의 투자 리그</h1>
          <p>매달 새로운 출발선에서, 나만의 선택으로 쌓아가는 기록.</p>
        </div>
        <select
          aria-label="시즌 선택"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {[...new Set([month, boot.month, ...seasons])]
            .sort()
            .reverse()
            .map((m: string) => (
              <option key={m} value={m}>
                {m.replace("-", "년 ")}월
              </option>
            ))}
        </select>
      </div>
      {data?.rivals && (
        <RivalComparison
          data={data.rivals}
          start={() => start("daily")}
          monthly
        />
      )}
      <section className="ranking-banner">
        <span className="trophy-illustration">
          <Trophy size={58} weight="duotone" />
        </span>
        <div>
          <h2>
            한 달의 도전은 끝나도,
            <br />
            나의 기록은 계속 남아요.
          </h2>
          <p>1·2·3위 칭호와 상위 10% 배지에 도전해 보세요.</p>
        </div>
        <span className="ranking-season">
          {month.slice(0, 4)}
          <strong>{month.slice(5)}</strong>SEASON
        </span>
      </section>
      {error && (
        <div role="alert" className="error-banner">
          {error}
          <button
            className="text-button"
            onClick={() => setRetry((value) => value + 1)}
          >
            다시 불러오기
          </button>
        </div>
      )}
      {!data ? (
        error ? null : (
          <Loader />
        )
      ) : (
        <section className="panel leaderboard">
          <div className="section-heading">
            <h2>이용자 월간 랭킹</h2>
            <span className="caption">누적 손익 기준 · 동점은 공동 순위</span>
          </div>
          {!data.rows.length ? (
            <Empty
              icon={Trophy}
              title="첫 번째 도전자가 되어보세요"
              body="오늘의 도전 한 판을 완료하면 월간 랭킹에 이름이 올라가요."
              action={
                <button className="btn primary" onClick={() => start("daily")}>
                  오늘의 도전 시작 <ArrowRight size={18} />
                </button>
              }
            />
          ) : (
            <>
              <div className="ranking-row ranking-labels">
                <span>순위</span>
                <span>투자자</span>
                <span>참여</span>
                <span>누적 손익</span>
              </div>
              {data.rows.map((r: any) => (
                <button
                  key={r.id}
                  className={`ranking-row ${r.id === boot.user.id ? "is-me" : ""}`}
                  onClick={() => profile(r.id)}
                >
                  <span className={r.rank <= 3 ? "medal-rank" : ""}>
                    {r.rank <= 3 ? (
                      <Medal size={27} weight="duotone" />
                    ) : (
                      r.rank
                    )}
                    {r.rank <= 3 && <small>{r.rank}</small>}
                  </span>
                  <span className="rank-user">
                    <Avatar name={r.name} />
                    <strong>{r.name}</strong>
                    {r.id === boot.user.id && <i>나</i>}
                  </span>
                  <span>{r.count}판</span>
                  <strong className={cn(r.profit)}>{signed(r.profit)}</strong>
                </button>
              ))}
            </>
          )}
        </section>
      )}
      <div className="inline-info">
        <Info size={18} />
        상위 10% 경계의 동점자는 모두 보상을 받아요. 랭킹은 투자 실력의 인증이
        아닌 게임 기록입니다.
      </div>
    </>
  );
}
function Records({ resume, start }: any) {
  const [tab, setTab] = useQueryParam("tab", "daily");
  const [data, setData] = useState<any>(null),
    [error, setError] = useState("");
  useEffect(() => {
    api("/records")
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);
  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return <Loader />;
  const rows = data.rounds.filter((r: any) => r.mode === tab),
    stats = data.training,
    total = stats.choices.reduce((s: number, a: any) => s + a.count, 0);
  return (
    <>
      <div className="page-heading">
        <div>
          <div className="date-line">작은 선택이 쌓이는 곳</div>
          <h1>나의 투자 기록</h1>
          <p>잘된 선택도, 아쉬웠던 순간도 모두 나의 경험이에요.</p>
        </div>
      </div>
      <div className="record-stats">
        <div>
          <span>완료한 자유 연습</span>
          <strong>
            {stats.count}
            <small>판</small>
          </strong>
        </div>
        <div>
          <span>평균 연습 수익률</span>
          <strong className={cn(stats.average)}>{pct(stats.average)}</strong>
        </div>
        <div>
          <span>최고 연습 수익률</span>
          <strong className={cn(stats.best)}>{pct(stats.best)}</strong>
        </div>
      </div>
      <section className="panel habit-panel">
        <h2>나의 선택 습관</h2>
        <div className="habits">
          {stats.choices.map((a: any) => (
            <div key={a.id}>
              <span>{a.label}</span>
              <strong>
                {total ? Math.round((a.count / total) * 100) : 0}
                <small>%</small>
              </strong>
              <small>{a.count}번 선택</small>
            </div>
          ))}
        </div>
        <p className="caption">
          연습 중 내가 고른 선택의 비율이에요. 투자 능력을 평가하는 점수는
          아니에요.
        </p>
      </section>
      <div className="section-heading records-heading">
        <h2>플레이 기록</h2>
        <div className="segmented">
          {["daily", "practice", "tutorial"].map((t) => (
            <button
              key={t}
              className={tab === t ? "selected" : ""}
              onClick={() => setTab(t)}
            >
              {modes[t]}
            </button>
          ))}
        </div>
      </div>
      <section className="panel record-list">
        {!rows.length ? (
          <Empty
            title="아직 쌓인 기록이 없어요"
            body="한 판을 시작하고 첫 번째 선택을 남겨보세요."
            action={
              <button className="btn primary" onClick={() => start(tab)}>
                시작하기
                <ArrowRight size={18} />
              </button>
            }
          />
        ) : (
          rows.map((r: any) => (
            <button key={r.id} onClick={() => resume(r.id)}>
              <span
                className={`record-icon ${r.status === "completed" ? "complete" : ""}`}
              >
                <ChartLineUp size={23} />
              </span>
              <span>
                <strong>
                  {modes[r.mode]} {r.mode === "daily" ? `${r.slot + 1}판` : ""}
                </strong>
                <small>
                  {r.day.replaceAll("-", ".")} · {r.answered}/5문제{" "}
                  {r.status === "abandoned"
                    ? "· 중단"
                    : r.status === "active"
                      ? "· 진행 중"
                      : "· 완료"}
                </small>
              </span>
              <span className={cn(r.assets - START)}>
                {r.status === "abandoned"
                  ? "성적 제외"
                  : pct((r.assets / START - 1) * 100)}
              </span>
              <CaretRight size={18} />
            </button>
          ))
        )}
      </section>
    </>
  );
}
function Community({ question, requireLogin, onError, notify, profile }: any) {
  // 커뮤니티 페이지에서는 글/글쓰기/탭을 URL에 기록해 뒤로가기와 새로고침에도 유지.
  // 게임 안의 토론(question)에서는 URL을 건드리지 않고 로컬 상태만 사용.
  const route = useContext(RouteContext);
  const routed = !question;
  const discussionQuestion = route.query.get("question") || undefined;
  const [posts, setPosts] = useState<any[]>([]),
    [loading, setLoading] = useState(true),
    [localCompose, setLocalCompose] = useState(false),
    [localSelected, setLocalSelected] = useState<string | null>(null),
    [comments, setComments] = useState<any[]>([]),
    [localTab, setLocalTab] = useState("전체"),
    [queryTab, setQueryTab] = useQueryParam("tab", "전체"),
    [report, setReport] = useState<any>(null),
    [sending, setSending] = useState(false);
  const [legacyLookup, setLegacyLookup] = useState<{
    id: string;
    status: "loading" | "locked" | "missing" | "error";
  } | null>(null);
  const compose = routed ? route.param === "new" : localCompose;
  const selectedId = routed
    ? route.param && route.param !== "new"
      ? route.param
      : null
    : localSelected;
  const tab = routed ? queryTab : localTab;
  const featured = tab === "전체" ? posts.find((p) => p.featured) : null;
  const categories = ["전체", "문제 토론", "투자 이야기", "결과 자랑", "자유"];
  const populated = (t: string) =>
    ["전체", "문제 토론"].includes(t) ||
    t === tab ||
    posts.some((p) => p.category === t);
  const renderTab = (t: string) => (
    <button
      key={t}
      className={tab === t ? "active" : ""}
      onClick={() => setTab(t)}
    >
      {t}
    </button>
  );
  const setTab = routed ? setQueryTab : setLocalTab;
  const setCompose = (on: boolean) => {
    if (!routed) setLocalCompose(on);
    else if (on) route.push("/community/new");
    else route.back("/community");
  };
  const load = useCallback(async () => {
    try {
      setPosts(
        await api(
          "/posts" +
            (question ? "?question=" + encodeURIComponent(question) : ""),
        ),
      );
    } catch (e: any) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [question, onError]);
  useEffect(() => {
    load();
  }, [load]);
  const mutate = async (fn: () => Promise<any>) => {
    if (sending) return;
    setSending(true);
    try {
      await fn();
      await load();
      if (selectedId) setComments(await api(`/posts/${selectedId}/comments`));
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSending(false);
    }
  };
  const open = (p: any) => {
    if (routed) route.push(`/community/${p.id}`);
    else setLocalSelected(p.id);
  };
  const close = () => {
    if (routed) route.back("/community");
    else setLocalSelected(null);
  };
  const back = () => {
    if (routed) route.back("/community");
    else {
      setLocalSelected(null);
      setLocalCompose(false);
    }
  };
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setComments([]);
    api(`/posts/${selectedId}/comments`)
      .then((c) => {
        if (!cancelled) setComments(Array.isArray(c) ? c : []);
      })
      .catch((e) => {
        if (!cancelled) onError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);
  useEffect(() => {
    if (!question) window.scrollTo({ top: 0, behavior: "instant" });
  }, [compose, selectedId, question]);
  const write = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    await mutate(async () => {
      await api("/posts", {
        title: form.get("title"),
        body: form.get("body"),
        category: form.get("category"),
        question,
      });
      setCompose(false);
      notify("이야기를 나눠주셔서 고마워요.");
    });
  };
  const like = (kind: string, id: string) => {
    if (!requireLogin()) return;
    mutate(() => api(`/content/${kind}/${id}/like`, {}));
  };
  const controls = (kind: string, p: any) => (
    <>
      <button
        className={`text-button ${p.liked ? "liked" : ""}`}
        aria-label="좋아요"
        onClick={() => like(kind, p.id)}
      >
        <Heart size={17} weight={p.liked ? "fill" : "regular"} />
        {p.likes}
      </button>
      {p.mine ? (
        <button
          className="icon-btn"
          aria-label="삭제"
          onClick={() =>
            mutate(async () => {
              await api(`/content/${kind}/${p.id}`, {}, "DELETE");
              if (kind === "posts") close();
              notify("삭제했어요.");
            })
          }
        >
          <Trash size={16} />
        </button>
      ) : (
        <>
          <button
            className="icon-btn"
            aria-label="신고"
            onClick={() => requireLogin() && setReport({ kind, id: p.id })}
          >
            <Flag size={16} />
          </button>
          <button
            className="icon-btn"
            aria-label="작성자 차단"
            onClick={() =>
              requireLogin() &&
              mutate(async () => {
                await api("/blocks", { user: p.author });
                close();
                notify("작성자를 차단했어요. 프로필에서 해제할 수 있어요.");
              })
            }
          >
            <Prohibit size={16} />
          </button>
        </>
      )}
    </>
  );
  useEffect(() => {
    if (
      !routed ||
      !selectedId ||
      loading ||
      posts.some((p) => p.id === selectedId)
    )
      return;
    let active = true;
    setLegacyLookup({ id: selectedId, status: "loading" });
    api(`/question-discussions/legacy/${encodeURIComponent(selectedId)}`)
      .then((data) => {
        if (active)
          route.replace(
            `/community?tab=${encodeURIComponent("문제 토론")}&question=${encodeURIComponent(data.question)}`,
          );
      })
      .catch((e: Error & { status?: number }) => {
        if (active)
          setLegacyLookup({
            id: selectedId,
            status:
              e.status === 404
                ? "missing"
                : e.status === 403
                  ? "locked"
                  : "error",
          });
      });
    return () => {
      active = false;
    };
  }, [selectedId, loading, posts, routed]);
  const current = posts.find((p) => p.id === selectedId);
  const backLabel = question ? "이야기 목록" : "커뮤니티";
  const reportModal = report && (
    <Modal title="신고하기" onClose={() => setReport(null)}>
      <form
        className="stack-form"
        onSubmit={(e) => {
          e.preventDefault();
          const reason = new FormData(e.currentTarget).get("reason");
          mutate(async () => {
            await api("/reports", { ...report, reason });
            setReport(null);
            notify("신고를 접수했어요. 운영자가 확인하겠습니다.");
          });
        }}
      >
        <label>
          신고 사유
          <textarea
            name="reason"
            required
            maxLength={500}
            rows={4}
            placeholder="문제가 되는 내용을 알려주세요."
          />
        </label>
        <button className="btn primary" disabled={sending}>
          신고 접수
        </button>
      </form>
    </Modal>
  );
  if (routed && (tab === "문제 토론" || discussionQuestion))
    return (
      <>
        <div className="page-heading">
          <h1>문제 토론</h1>
          <p>같은 문제를 푼 사람들의 생각을 나눠요.</p>
        </div>
        <button
          className="text-button"
          onClick={() => route.push("/community")}
        >
          일반 커뮤니티로
        </button>
        {discussionQuestion && (
          <button
            className="text-button"
            onClick={() =>
              route.push(`/community?tab=${encodeURIComponent("문제 토론")}`)
            }
          >
            문제 토론 목록으로
          </button>
        )}
        <QuestionDiscussion
          question={discussionQuestion}
          onSelect={(question) =>
            route.push(
              `/community?tab=${encodeURIComponent("문제 토론")}&question=${encodeURIComponent(question)}`,
            )
          }
          requireLogin={requireLogin}
          onError={onError}
          notify={notify}
        />
      </>
    );
  if (compose)
    return (
      <>
        <button className="back-button community-back" onClick={back}>
          <ArrowLeft size={18} />
          {backLabel}
        </button>
        <div className={question ? "section-heading" : "page-heading"}>
          <div>
            {!question && (
              <div className="date-line">혼자 고민하지 않아도 괜찮아요</div>
            )}
            {question ? (
              <h2>투자 이야기 나누기</h2>
            ) : (
              <h1>투자 이야기 나누기</h1>
            )}
            {!question && (
              <p>나의 선택, 궁금한 점, 소소한 이야기까지 편하게 나눠요.</p>
            )}
          </div>
        </div>
        <section className="panel post-compose">
          <form onSubmit={write} className="stack-form">
            <label>
              이야기 주제
              <select name="category">
                <option>자유</option>
                <option>투자 이야기</option>
                <option>결과 자랑</option>
              </select>
            </label>
            <label>
              제목
              <input
                name="title"
                required
                maxLength={80}
                placeholder="어떤 이야기를 나누고 싶나요?"
              />
            </label>
            <label>
              내용
              <textarea
                name="body"
                required
                maxLength={2000}
                rows={10}
                placeholder="나의 생각을 편하게 적어주세요."
              />
            </label>
            <div className="compose-actions">
              <button type="button" className="btn outline" onClick={back}>
                취소
              </button>
              <button className="btn primary" disabled={sending}>
                이야기 올리기
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        </section>
      </>
    );
  if (selectedId && loading) return <Loader />;
  if (selectedId && !current) {
    if (
      !legacyLookup ||
      legacyLookup.id !== selectedId ||
      legacyLookup.status === "loading"
    )
      return <Loader />;
    const locked = legacyLookup.status === "locked";
    const missing = legacyLookup.status === "missing";
    return (
      <>
        <button className="back-button community-back" onClick={back}>
          <ArrowLeft size={18} />
          {backLabel}
        </button>
        <Empty
          icon={ChatsCircle}
          title={
            locked
              ? "풀이 후 확인할 수 있어요"
              : missing
                ? "게시글을 볼 수 없어요"
                : "이야기를 불러오지 못했어요"
          }
          body={
            locked
              ? "해당 문제에 직접 답변한 뒤 투자 이야기를 나눌 수 있어요."
              : missing
                ? "아직 공개되지 않았거나 삭제된 글이에요. 다른 이야기를 둘러보세요."
                : "잠시 후 새로고침해 주세요."
          }
        />
      </>
    );
  }
  if (selectedId && current)
    return (
      <>
        <button className="back-button community-back" onClick={back}>
          <ArrowLeft size={18} />
          {backLabel}
        </button>
        <article className="panel post-detail">
          <div className="post-author">
            {profile && !current.official ? (
              <button
                className="author-button"
                onClick={() => profile(current.author)}
              >
                <Avatar name={current.authorName} size="small" />
                <span>{current.authorName}</span>
              </button>
            ) : (
              <>
                <Avatar name={current.authorName} size="small" />
                <span>{current.authorName}</span>
              </>
            )}
            <time>
              {new Date(current.created).toLocaleDateString("ko-KR", {
                timeZone: "Asia/Seoul",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
            {current.official && <span className="official-badge">운영진</span>}
            <span className="tag">{current.category}</span>
          </div>
          <h1 className="post-detail-title">{current.title}</h1>
          <p className="full-post-body">{current.body}</p>
          <div className="post-actions">
            {controls("posts", current)}
            <span className="text-button">
              <ChatCircle size={17} />
              {comments.length}
            </span>
          </div>
          <h3 className="comment-heading">댓글 {comments.length}</h3>
          {comments.map((c) => (
            <div className="comment" key={c.id}>
              <div className="post-author">
                <Avatar name={c.authorName} size="small" />
                <strong>{c.authorName}</strong>
              </div>
              <p>{c.body}</p>
              <div className="post-actions">{controls("comments", c)}</div>
            </div>
          ))}
          <form
            className="comment-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!requireLogin()) return;
              const form = e.currentTarget,
                body = new FormData(form).get("body");
              mutate(async () => {
                await api(`/posts/${selectedId}/comments`, { body });
                form.reset();
              });
            }}
          >
            <label className="sr-only" htmlFor="comment-body">
              댓글
            </label>
            <textarea
              id="comment-body"
              name="body"
              required
              rows={2}
              maxLength={1000}
              placeholder="서로의 선택을 존중하며 이야기해요."
            />
            <button className="btn primary compact" disabled={sending}>
              등록
            </button>
          </form>
        </article>
        {reportModal}
      </>
    );
  return (
    <>
      <div className={question ? "section-heading" : "page-heading"}>
        <div>
          {!question && (
            <div className="date-line">혼자 고민하지 않아도 괜찮아요</div>
          )}
          {question ? (
            <h2>이 문제의 투자 이야기</h2>
          ) : (
            <h1>함께 나누는 투자 이야기</h1>
          )}
          {!question && (
            <p>나의 선택, 궁금한 점, 소소한 이야기까지 편하게 나눠요.</p>
          )}
        </div>
        <button
          className="btn primary compact"
          onClick={() => requireLogin() && setCompose(true)}
        >
          <PencilSimple size={18} />
          글쓰기
        </button>
      </div>
      {!question && (
        <div className="community-filters">
          <div className="filter-tabs">
            {categories.filter(populated).map(renderTab)}
          </div>
          {categories.some((t) => !populated(t)) && (
            <details className="community-extra-filters">
              <summary>더 많은 주제</summary>
              <div className="filter-tabs">
                {categories.filter((t) => !populated(t)).map(renderTab)}
              </div>
            </details>
          )}
        </div>
      )}
      {!loading && featured && (
        <section className="panel community-featured">
          <span className="official-badge">운영진 · 오늘의 토론</span>
          <h2>{featured.title}</h2>
          <p>{featured.body}</p>
          <button
            className="btn outline compact"
            onClick={() => open(featured)}
          >
            내 생각 나누기
          </button>
        </section>
      )}
      {loading ? (
        <Loader />
      ) : !posts.filter((p) => tab === "전체" || p.category === tab).length ? (
        <Empty
          icon={ChatsCircle}
          title="첫 이야기를 들려주세요"
          body="어떤 이유로 선택했나요? 다른 사람과 생각을 나눠보세요."
        />
      ) : (
        <div className="post-list">
          {posts
            .filter(
              (p) =>
                (tab === "전체" || p.category === tab) && p.id !== featured?.id,
            )
            .map((p) => (
              <article className="panel post-card" key={p.id}>
                <div className="post-author">
                  {p.official ? (
                    <>
                      <Avatar name={p.authorName} size="small" />
                      <span>{p.authorName}</span>
                      <span className="official-badge">운영진</span>
                    </>
                  ) : (
                    <button
                      className="author-button"
                      onClick={() => (profile ? profile(p.author) : open(p))}
                    >
                      <Avatar name={p.authorName} size="small" />
                      <span>{p.authorName}</span>
                    </button>
                  )}
                  <time>
                    {new Date(p.created).toLocaleDateString("ko-KR", {
                      timeZone: "Asia/Seoul",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                  <span className="tag">{p.category}</span>
                </div>
                <button className="post-body-button" onClick={() => open(p)}>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </button>
                <div className="post-actions">
                  {controls("posts", p)}
                  <button className="text-button" onClick={() => open(p)}>
                    <ChatCircle size={17} />
                    {p.comments}
                  </button>
                </div>
              </article>
            ))}
        </div>
      )}
      {reportModal}
    </>
  );
}
function Profile({ id, mine, refresh, notify, onError, logout }: any) {
  const [data, setData] = useState<any>(null),
    [edit, setEdit] = useState(false),
    [blocks, setBlocks] = useState<any[]>([]);
  const load = useCallback(
    () =>
      api("/profile/" + encodeURIComponent(id))
        .then(setData)
        .catch((e: any) => onError(e.message)),
    [id, onError],
  );
  useEffect(() => {
    load();
    if (mine)
      api("/blocks")
        .then(setBlocks)
        .catch(() => {});
  }, [load, mine]);
  if (!data) return <Loader />;
  return (
    <>
      <div className="page-heading">
        <div>
          <div className="date-line">시간이 지나도 남는 나의 도전</div>
          <h1>투자 프로필</h1>
        </div>
        {mine && (
          <button className="btn outline compact" onClick={logout}>
            <SignOut size={17} />
            로그아웃
          </button>
        )}
      </div>
      <section className="panel profile-card">
        <Avatar name={data.name} size="large" />
        <div>
          <h2>
            {data.name}
            {mine && (
              <button
                className="icon-btn"
                aria-label="닉네임 수정"
                onClick={() => setEdit(true)}
              >
                <PencilSimple size={18} />
              </button>
            )}
          </h2>
          <p>
            {new Date(data.joined).toLocaleDateString("ko-KR")}부터 함께했어요
          </p>
        </div>
        <div className="profile-current">
          <span>이번 달 순위</span>
          <strong>{data.current ? `${data.current.rank}위` : "도전 전"}</strong>
        </div>
      </section>
      <div className="section-heading">
        <h2>나의 시즌 컬렉션</h2>
        <span className="caption">월별 순위와 보상은 계속 보존돼요</span>
      </div>
      {!data.seasons.length ? (
        <section className="panel">
          <Empty
            icon={Medal}
            title="나만의 시즌을 채워가세요"
            body="시즌이 끝나면 최종 순위와 획득한 배지가 이곳에 남아요."
          />
        </section>
      ) : (
        <div className="season-archive">
          {data.seasons
            .sort((a: any, b: any) => b.month.localeCompare(a.month))
            .map((s: any) => (
              <section className="panel" key={s.month}>
                <Medal size={36} weight="duotone" />
                <h3>{s.month.replace("-", "년 ")}월</h3>
                <strong>
                  {s.participants.toLocaleString()}명 중 {s.rank}위
                </strong>
                <div className="badges">
                  {s.badges.map((b: string) => (
                    <span key={b} className="tag">
                      {b}
                    </span>
                  ))}
                </div>
              </section>
            ))}
        </div>
      )}
      {mine && blocks.length > 0 && (
        <section className="panel blocked-list">
          <h2>차단한 사용자</h2>
          {blocks.map((b) => (
            <div key={b.target}>
              <span>{b.name}</span>
              <button
                className="text-button"
                onClick={async () => {
                  try {
                    await api(
                      "/blocks/" + encodeURIComponent(b.target),
                      {},
                      "DELETE",
                    );
                    setBlocks(blocks.filter((x) => x.target !== b.target));
                    notify("차단을 해제했어요.");
                  } catch (e: any) {
                    onError(e.message);
                  }
                }}
              >
                차단 해제
              </button>
            </div>
          ))}
        </section>
      )}
      {edit && (
        <Modal title="닉네임 수정" onClose={() => setEdit(false)}>
          <form
            className="stack-form"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api(
                  "/profile",
                  { name: new FormData(e.currentTarget).get("name") },
                  "PATCH",
                );
                await load();
                await refresh();
                setEdit(false);
                notify("닉네임을 변경했어요.");
              } catch (e: any) {
                onError(e.message);
              }
            }}
          >
            <label>
              닉네임
              <input
                name="name"
                defaultValue={data.name}
                required
                minLength={2}
                maxLength={16}
              />
            </label>
            <button className="btn primary">저장하기</button>
          </form>
        </Modal>
      )}
    </>
  );
}
function Notifications({ refresh, navigate }: any) {
  const [items, setItems] = useState<any[] | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    api("/notifications")
      .then(async (n) => {
        setItems(n);
        await api("/notifications/read", {});
        refresh();
      })
      .catch((e) => setError(e.message));
  }, [refresh]);
  return (
    <>
      <div className="page-heading">
        <div>
          <h1>알림</h1>
          <p>나의 도전과 대화의 새로운 소식을 확인해요.</p>
        </div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {!items ? (
        <Loader />
      ) : !items.length ? (
        <section className="panel">
          <Empty
            icon={Bell}
            title="아직 새로운 소식이 없어요"
            body="댓글과 시즌 결과가 도착하면 알려드릴게요."
          />
        </section>
      ) : (
        <section className="panel notification-list">
          {items.map((n, i) => (
            <button key={i} onClick={() => navigate(n.link)}>
              <span className="soft-icon">
                <Bell size={21} />
              </span>
              <span>
                <strong>{n.text}</strong>
                <small>{new Date(n.created).toLocaleString("ko-KR")}</small>
              </span>
              {!n.read && <span className="unread-dot" />}
              <CaretRight size={18} />
            </button>
          ))}
        </section>
      )}
    </>
  );
}
